// ProductSelectionEditor.tsx

import * as React from 'react';
import { Card, Select, InputNumber, Spin, Alert, Divider, Typography } from 'antd';
import { SelectionOptionEditor } from 'app/pages/DiscountEditor/components/ProductSelection/SelectionOptions';
import { StackedTransferSelector } from 'app/pages/DiscountEditor/components/StackedTransferSelector';
import * as ProductFilter from 'app/shared/model/ProductFilter';
import { ProductsListState } from 'app/pages/DiscountEditor/modules/ProductSelection';
import { UnitsFilterType, UnitsFilter } from 'app/shared/model/ProductUnitFilter';
import { ProductFilterAllowedOptions } from 'app/pages/DiscountEditor/components/ProductSelection/ProductFilterAllowedOptions';
import { Product, ProductType } from 'app/shared/model/Product';

const { Text } = Typography;

export interface ProductSelectionEditorProps {
  productFilter: ProductFilter.ProductFilter;
  changeProductFilter: (newFilter: ProductFilter.ProductFilter) => void;
  unitFilter: UnitsFilter;
  changeUnitFilter?: (unitFilter: UnitsFilter) => void;
  allowUnitFilter: boolean;
  allowedFilterOption?: ProductFilterAllowedOptions;
  errorMessage?: string;
  productOptions: Product[];
  productsListState: ProductsListState;
  productOfferingHints?: { [productCode: string]: string };
}

/**
 * Unit filter selector with numeric inputs
 */
const UnitFilter: React.FC<ProductSelectionEditorProps> = props => {
  const unitsOptions = [
    { label: "doesn't matter", value: UnitsFilterType.None },
    { label: 'must be at least', value: UnitsFilterType.MinUnits },
    { label: 'must be at most', value: UnitsFilterType.MaxUnits },
    { label: 'must be between', value: UnitsFilterType.BetweenUnits },
  ];

  const { unitFilter, changeUnitFilter } = props;
  if (!changeUnitFilter) return null;

  const showMin = unitFilter.type === UnitsFilterType.MinUnits || unitFilter.type === UnitsFilterType.BetweenUnits;
  const showMax = unitFilter.type === UnitsFilterType.MaxUnits || unitFilter.type === UnitsFilterType.BetweenUnits;

  return (
    <div style={{ display: 'flex', alignItems: 'center', margin: '16px 0' }}>
      <Text>The number of units (week)</Text>
      <Select
        style={{ width: 180, margin: '0 8px' }}
        options={unitsOptions}
        value={unitFilter.type}
        onChange={(value: UnitsFilterType) => changeUnitFilter({ ...unitFilter, type: value })}
        popupMatchSelectWidth={false}
        getPopupContainer={(triggerNode: { parentElement: any; }) => triggerNode.parentElement!}
      />
      {showMin && (
        <InputNumber
          min={0}
          value={unitFilter.minUnits}
          onChange={(value: number | null) => changeUnitFilter({ ...unitFilter, minUnits: value ?? 0 })}
          style={{ width: 80, marginRight: 8 }}
        />
      )}
      {unitFilter.type === UnitsFilterType.BetweenUnits && <Text>and</Text>}
      {showMax && (
        <InputNumber
          min={0}
          value={unitFilter.maxUnits}
          onChange={(value: number | null) => changeUnitFilter({ ...unitFilter, maxUnits: value ?? 0 })}
          style={{ width: 80, marginLeft: 8 }}
        />
      )}
    </div>
  );
};

/**
 * Area for selecting product checkboxes or showing messages
 */
const ProductsSelectionArea: React.FC<ProductSelectionEditorProps> = props => {
  const { productFilter, productOptions, productsListState, changeProductFilter } = props;
  const needsSpecificSelection = ProductFilter.filterNeedsProductCodes(productFilter.type);

  if (!needsSpecificSelection) {
    return null;
  }

  const selectedMap = ProductFilter.getSelectedProductCodes(productFilter);
  const updateSelection = (code: string, checked: boolean) => {
    const newFilter = ProductFilter.changeProductSelection(productFilter, code, checked);
    if (newFilter !== productFilter) changeProductFilter(newFilter);
  };

  switch (productsListState) {
    case ProductsListState.Loading:
      return <Spin />;

    case ProductsListState.Show:
      const transferItems = productOptions.map(product => ({
        key: product.code,
        title: product.name,
        description: props.productOfferingHints?.[product.code],
      }));

      return (
        <StackedTransferSelector
          items={transferItems}
          targetKeys={Object.keys(selectedMap).filter(code => !!selectedMap[code])}
          onChange={nextKeys => {
            const nextSelected = new Set(nextKeys);
            const currentSelected = new Set(
              Object.keys(selectedMap).filter(code => !!selectedMap[code])
            );
            let nextFilter = productFilter;

            productOptions.forEach(product => {
              const isSelected = currentSelected.has(product.code);
              const shouldBeSelected = nextSelected.has(product.code);
              if (isSelected !== shouldBeSelected) {
                nextFilter = ProductFilter.changeProductSelection(
                  nextFilter,
                  product.code,
                  shouldBeSelected
                );
              }
            });

            if (nextFilter !== productFilter) {
              changeProductFilter(nextFilter);
            }
          }}
          availableTitle="All items"
          selectedTitle="Selected items"
          searchPlaceholder="Search by name..."
          height={180}
        />
      );

    case ProductsListState.NoSchools:
      return (
        <Alert
          type="info"
          title="School Required"
          description="Please select at least one school."
          showIcon
          style={{ margin: '16px 0' }}
        />
      );

    case ProductsListState.Error:
    default:
      return (
        <Alert
          type="error"
          title="Products Error"
          description="Unable to load products list."
          showIcon
          style={{ margin: '16px 0' }}
        />
      );
  }
};

/**
 * Main editor component
 */
export const ProductSelectionEditor: React.FC<ProductSelectionEditorProps> = props => (
  <Card style={{ padding: 16 }}>
    {props.errorMessage && (
      <Alert
        type="error"
        title={props.errorMessage}
        banner
        showIcon
        style={{ marginBottom: 16 }}
      />
    )}

    <SelectionOptionEditor
      productFilter={props.productFilter}
      allowedFilterOptions={props.allowedFilterOption ?? ProductFilterAllowedOptions.All}
      changeProductFilter={props.changeProductFilter}
    />

    {props.allowUnitFilter && <UnitFilter {...props} />}

    <Divider />

    <ProductsSelectionArea {...props} />
  </Card>
);
